import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import SvgIcon from "components/Common/SvgIcon";
import { NO_OP_FUNCTION } from "services/helpers";

import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

const BLOCK_NAME = "InputSearch";

class InputSearch extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.clearInput = this.clearInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  /**
   */
  componentDidMount() {
    const { autoFocus } = this.props;

    if (autoFocus) {
      this.inputRef.current.focus();
    }
  }

  /**
   * Handle clearing the component state when clearing.
   * Need to do this since the onChange is debounced
   */
  clearInput() {
    const { onClear } = this.props;
    onClear();
  }

  /**
   * @param {Event} event
   */
  handleChange(event) {
    const { onChange } = this.props;
    event.persist();
    onChange(event);
  }

  /**
   * Conditionally render either dropdown chevron or clear searc icon
   */
  renderRightSideIcon() {
    const {
      value,
      onClear,
      hideChevron,
      invalid
    } = this.props;

    if (value && onClear) {
      return (
        <>
          <div
            className={classNames(`${BLOCK_NAME}__gradient`, {
              [`${BLOCK_NAME}__gradient--invalid`]: invalid
            })}
          />
          {/* Not going to have a keyboard handler */}
          <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, max-len
            role="button"
            className={`${BLOCK_NAME}__icon-container`}
            onClick={this.clearInput}
          >
            <SvgIcon
              icon="Clear"
              customClassName={`${BLOCK_NAME}__icon-container__icon`}
              fillColor="currentColor"
            />
          </div>
        </>
      );
    }

    if (!hideChevron) {
      return (
        <div className={`${BLOCK_NAME}__icon-container InputSearch__icon-container--type-chevron`}>
          <SvgIcon
            icon="ChevronDown"
            customClassName={`${BLOCK_NAME}__icon-container__icon`}
            fillColor="currentColor"
          />
        </div>
      );
    }

    return null;
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      customClassName,
      invalid,
      idForLabel,
      placeholder,
      onFocus,
      onKeyPress,
      onKeyDown,
      onBlur,
      value,
      onClear
    } = this.props;

    return (
      <div
        className={classNames(`${BLOCK_NAME}`, {
          [customClassName]: customClassName
        })}
      >
        <input
          className={classNames(`${BLOCK_NAME}__input`, "g-input", {
            "g-input--invalid": invalid,
            [`${BLOCK_NAME}__input--right-icon`]: onClear
          })}
          id={idForLabel}
          value={value}
          type="text"
          placeholder={placeholder || "Search"}
          onChange={this.handleChange}
          onFocus={onFocus}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          autoComplete="off"
          spellCheck={false}
          ref={this.inputRef}
          data-prevent-drag-focus={false}
        />
        <div className={`${BLOCK_NAME}__search-icon-container`}>
          <SvgIcon
            icon="Search"
            customClassName={`${BLOCK_NAME}__search-icon-container__icon`}
            fillColor={colors.colorGrey4}
          />
        </div>
        {value && onClear && <div className={`${BLOCK_NAME}__gradient`} />}
        {this.renderRightSideIcon()}
      </div>
    );
  }
}

InputSearch.propTypes = {
  autoFocus: PropTypes.bool,
  value: PropTypes.string,
  onClear: PropTypes.func,
  hideChevron: PropTypes.bool,
  customClassName: PropTypes.string,
  invalid: PropTypes.bool,
  idForLabel: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func
};

InputSearch.defaultProps = {
  autoFocus: false,
  value: undefined,
  onClear: null,
  hideChevron: true,
  customClassName: null,
  invalid: false,
  idForLabel: null,
  placeholder: null,
  onChange: NO_OP_FUNCTION,
  onFocus: NO_OP_FUNCTION,
  onKeyPress: NO_OP_FUNCTION,
  onKeyDown: NO_OP_FUNCTION,
  onBlur: NO_OP_FUNCTION
};

export default InputSearch;
